import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState, downloadExport } from "@/components/PageToolbar";
import { RefreshCw, FileSpreadsheet, FileText, Store, Wallet } from "lucide-react";
import api2 from "@/lib/api";
import { formatBill } from "@/lib/format";
import Pagination, { usePagination } from "@/components/Pagination";
import ImportButton from "@/components/ImportButton";

const RANGES = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Last 90 Days", "This Month", "Custom"];

function rangeToDates(rng) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const start = new Date(today);
  switch (rng) {
    case "Today":         return { from: iso(today), to: iso(today) };
    case "Yesterday":     start.setDate(today.getDate() - 1); return { from: iso(start), to: iso(start) };
    case "Last 7 Days":   start.setDate(today.getDate() - 6); return { from: iso(start), to: iso(today) };
    case "Last 30 Days":  start.setDate(today.getDate() - 29); return { from: iso(start), to: iso(today) };
    case "Last 90 Days":  start.setDate(today.getDate() - 89); return { from: iso(start), to: iso(today) };
    case "This Month":    return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    default:              return null;
  }
}

export default function MISReport() {
  const { user: currentUser } = useAuth();
  const isBranch = currentUser?.role === "branch";
  const today = new Date().toISOString().slice(0, 10);
  const past = new Date();
  past.setDate(past.getDate() - 89);
  const [type, setType] = useState("Service");
  const [subtype, setSubtype] = useState("all");
  const [rng, setRng] = useState("Last 90 Days");
  const [dateFrom, setDateFrom] = useState(past.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");
  const [balanceOnly, setBalanceOnly] = useState(false);

  // Balance view is active if user selects "Balance" subtype OR ticks the checkbox
  const isBalanceView = subtype === "balance" || balanceOnly;

  const paginationKey = `${type}|${subtype}|${rng}|${dateFrom}|${dateTo}|${branchId}|${isBalanceView}`;
  const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, paginationKey);

  // When user picks a preset range, auto-sync the date pickers so they match
  useEffect(() => {
    const d = rangeToDates(rng);
    if (d) { setDateFrom(d.from); setDateTo(d.to); }
    // "Custom" keeps whatever dates the user typed
  }, [rng]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mis-report", {
        params: { type, subtype, rng, date_from: dateFrom, date_to: dateTo, branch_id: branchId, balance_only: isBalanceView },
      });
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (!isBranch) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    load();
    /* eslint-disable-next-line */
  }, []);

  // Re-fetch automatically when Balance mode toggles (either via subtype or checkbox)
  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, [isBalanceView]);

  const downloadPdf = async () => {
    const res = await api2.get(`/export-pdf/mis-report`, {
      params: { rng, date_from: dateFrom, date_to: dateTo, type, branch_id: branchId, balance_only: isBalanceView },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `mis-report${isBalanceView ? "-balance" : ""}-${dateFrom}_to_${dateTo}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = async () => {
    const res = await api2.get(`/export/mis-report`, {
      params: { rng, date_from: dateFrom, date_to: dateTo, type, branch_id: branchId, balance_only: isBalanceView },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `mis-report${isBalanceView ? "-balance" : ""}-${dateFrom}_to_${dateTo}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  // Paid semantics: Service rows treat the initial cash as Advance only; Paid shows only balance collections (Receipt-type rows).
  const displayPaid = (r) => (r.type === "Service" ? 0 : Number(r.paid_amount || 0));
  const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + displayPaid(r), 0);
  const totalAdvance = rows.reduce((s, r) => s + Number(r.advance || 0), 0);
  const totalBalance = rows.reduce((s, r) => s + Number(r.balance || 0), 0);

  return (
    <div data-testid="mis-report-page">
      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9 w-32 bg-amber-50 border-amber-300" data-testid="mis-type"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="Service">Service</SelectItem>
            <SelectItem value="Receipt">Receipt</SelectItem>
            <SelectItem value="All">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={subtype} onValueChange={setSubtype}>
          <SelectTrigger className="h-9 w-32" data-testid="mis-subtype"><SelectValue placeholder="-- Select --" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">-- Select --</SelectItem>
            <SelectItem value="detail">Detail</SelectItem>
            <SelectItem value="summary">Summary</SelectItem>
            <SelectItem value="balance">Balance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={rng} onValueChange={setRng}>
          <SelectTrigger className="h-9 w-36" data-testid="mis-range"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {RANGES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="mis-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="mis-to" />
        {!isBranch && (
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="h-9 w-44" data-testid="mis-branch">
              <Store className="h-3.5 w-3.5 mr-1.5 text-slate-500" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">— All Branches —</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button onClick={load} disabled={loading} className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="mis-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button onClick={downloadExcel} className="h-9 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white font-semibold" data-testid="mis-excel">
          <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
        </Button>
        <Button onClick={downloadPdf} className="h-9 bg-red-500 hover:bg-red-600 text-white font-semibold" data-testid="mis-pdf">
          <FileText className="h-4 w-4 mr-1.5" /> PDF
        </Button>
        {currentUser?.role === "admin" && (
          <Button
            onClick={async () => {
              try {
                const res = await api2.get(`/export/mis-report?all=true`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url; a.download = `mis-report-ALL.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) { /* ignore */ }
            }}
            className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            data-testid="mis-excel-all"
            title="Admin: download every historical record in one click (ignores filters)"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export ALL
          </Button>
        )}
        {currentUser?.role === "admin" && (
          <ImportButton resource="receipts" onDone={load} label="Import Excel" />
        )}
        <label className="flex items-center gap-1.5 pl-2 pr-2 h-9 rounded-md border border-slate-200 bg-slate-50 cursor-pointer select-none" data-testid="mis-balance-only-wrap">
          <Checkbox checked={balanceOnly} onCheckedChange={(v) => setBalanceOnly(!!v)} data-testid="mis-balance-only" />
          <Wallet className="h-3.5 w-3.5 text-red-500" />
          <span className="text-xs font-semibold text-slate-700">Only Pending Balance</span>
        </label>
        <div className="flex-1" />
        <div className="flex gap-4">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Records</div>
            <div className="font-mono-num font-bold">{rows.length}</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Total Amount</div>
            <div className="font-display font-black text-sm font-mono-num text-[color:var(--brand-navy)]">{inr(totalAmount)}</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Advance</div>
            <div className="font-display font-black text-sm font-mono-num text-amber-700">{inr(totalAdvance)}</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Paid</div>
            <div className="font-display font-black text-sm font-mono-num text-emerald-700">{inr(totalPaid)}</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Balance</div>
            <div className="font-display font-black text-sm font-mono-num text-red-600">{inr(totalBalance)}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          {isBalanceView ? (
            <table className="w-full text-sm crm-table" data-testid="mis-balance-table">
              <thead className="bg-red-50 text-red-700 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Mobile</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Doc No</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Service</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Advance</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((r) => (
                  <tr key={r.id} data-testid="mis-balance-row">
                    <td className="px-3 py-2 font-mono-num text-slate-600">{r.doc_date}</td>
                    <td className="px-3 py-2 font-semibold">{r.customer_name}</td>
                    <td className="px-3 py-2 font-mono-num text-slate-600">{r.mobile}</td>
                    <td className="px-3 py-2 font-mono-num">{formatBill(r.doc_no)}</td>
                    <td className="px-3 py-2">{r.service_name}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{inr(r.amount)}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-amber-700">{inr(r.advance)}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{inr(displayPaid(r))}</td>
                    <td className="px-3 py-2 text-right font-mono-num font-bold text-red-600">{inr(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="bg-red-600 text-white font-semibold" data-testid="mis-balance-totals-row">
                    <td colSpan={5} className="px-3 py-3 text-right uppercase tracking-widest text-[11px]">Pending Balance Totals</td>
                    <td className="px-3 py-3 text-right font-mono-num text-sm">{inr(totalAmount)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-sm text-amber-200">{inr(totalAdvance)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-sm text-emerald-200">{inr(totalPaid)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-sm">{inr(totalBalance)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
          <table className="w-full text-sm crm-table">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Type</th>
                <th className="px-3 py-2.5 text-left font-semibold">Doc No</th>
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold">Service</th>
                <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
                <th className="px-3 py-2.5 text-left font-semibold">Mobile</th>
                <th className="px-3 py-2.5 text-left font-semibold">Payment</th>
                <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                <th className="px-3 py-2.5 text-right font-semibold">Advance</th>
                <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
                <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                <th className="px-3 py-2.5 text-left font-semibold">Login By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 text-[color:var(--brand-teal)] font-semibold">{r.type}</td>
                  <td className="px-3 py-2 font-mono-num">{formatBill(r.doc_no)}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{r.doc_date}</td>
                  <td className="px-3 py-2">{r.service_name}</td>
                  <td className="px-3 py-2">{r.customer_name}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{r.mobile}</td>
                  <td className="px-3 py-2 text-slate-600">{r.payment_type}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{inr(r.amount)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{inr(r.advance)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(displayPaid(r))}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{inr(r.balance)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.supervisor || r.operator || "—"}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-[color:var(--brand-navy)] text-white font-semibold" data-testid="mis-totals-row">
                  <td colSpan={7} className="px-3 py-3 text-right uppercase tracking-widest text-[11px]">Totals</td>
                  <td className="px-3 py-3 text-right font-mono-num text-sm">{inr(totalAmount)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-sm text-amber-300">{inr(totalAdvance)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-sm text-emerald-300">{inr(totalPaid)}</td>
                  <td className="px-3 py-3 text-right font-mono-num text-sm text-red-300">{inr(totalBalance)}</td>
                  <td className="px-3 py-3 text-right text-[10px] uppercase tracking-widest text-slate-300">{rows.length} rec.</td>
                </tr>
              </tfoot>
            )}
          </table>
          )}
          {rows.length === 0 && <EmptyState label="No entries in selected range" />}
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid="mis-pagination" />
      </div>
    </div>
  );
}
